import { sourceLink } from "@/lib/source-link";
import { CITATION_SOURCE_ID_PATTERN } from "@shaarp/common";

/**
 * A web page an answer may cite: what a search returned, keyed by the id the
 * assistant cites it by. `snippet` is the passage the search kept, present
 * while the answer streams and, after a reload, only for a page that was cited.
 */
export type CitationSource = {
  readonly id: string;
  readonly title: string;
  /** The page's host without `www.`, to show. */
  readonly host: string;
  /** The URL as it is linked: the parsed one, http(s) only. */
  readonly href: string;
  readonly snippet?: string;
};

type SourceEntry = {
  readonly id?: unknown;
  readonly title?: unknown;
  readonly url?: unknown;
  readonly snippet?: unknown;
};

type MessageWithParts = {
  readonly parts: ReadonlyArray<{ readonly type: string }>;
};

const asText = (value: unknown): string =>
  typeof value === "string" ? value : "";

/** What a research part's output holds that may be cited. */
type ResearchOutput = {
  readonly sources?: unknown;
  readonly pages?: unknown;
};

/**
 * The output objects of the research tool parts of a conversation, in order —
 * live or persisted: it is the same payload either way.
 *
 * `useChat` copies the parts of the message being streamed on every delta but
 * keeps each part's `output` object, so an output is the key a read of its
 * sources can be cached on, and the list of outputs tells a search that landed
 * from a mere delta.
 */
export function researchOutputsOf(
  messages: ReadonlyArray<MessageWithParts>,
): object[] {
  const outputs: object[] = [];
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type !== "tool-webSearch" && part.type !== "tool-readPages") {
        continue;
      }
      const output = (part as { output?: unknown }).output;
      if (output && typeof output === "object") outputs.push(output);
    }
  }
  return outputs;
}

const sourcesByOutput = new WeakMap<object, ReadonlyArray<CitationSource>>();

/**
 * The plausible sources of one research output, read once per output object.
 * Left out: an id that is not the cited shape (a source stored before
 * citations existed carries its search rank), a URL that is not http(s).
 */
function citableSourcesOf(output: object): ReadonlyArray<CitationSource> {
  const cached = sourcesByOutput.get(output);
  if (cached) return cached;

  const { sources, pages } = output as ResearchOutput;
  const entries = [sources, pages].flatMap((list) =>
    Array.isArray(list) ? list : [],
  );
  const read: CitationSource[] = [];
  for (const entry of entries as ReadonlyArray<SourceEntry | null>) {
    const id = asText(entry?.id);
    if (!CITATION_SOURCE_ID_PATTERN.test(id)) continue;
    const link = sourceLink(asText(entry?.url));
    if (!link) continue;
    const snippet = asText(entry?.snippet).trim();
    read.push({
      id,
      title: asText(entry?.title).trim() || link.host,
      host: link.host,
      href: link.href,
      ...(snippet && { snippet }),
    });
  }
  sourcesByOutput.set(output, read);
  return read;
}

/**
 * The citable pages of the research outputs, by id.
 *
 * An id is only ever resolved to a page a search returned, so the link a pill
 * opens never comes from the model. When two entries share an id, the first
 * with an excerpt wins, then the first seen.
 */
export function citationSourcesOf(
  outputs: ReadonlyArray<object>,
): Map<string, CitationSource> {
  const sources = new Map<string, CitationSource>();
  for (const output of outputs) {
    for (const source of citableSourcesOf(output)) {
      const known = sources.get(source.id);
      if (known && (known.snippet || !source.snippet)) continue;
      sources.set(source.id, source);
    }
  }
  return sources;
}

/** The citable pages of a conversation, by id: {@link citationSourcesOf}. */
export function collectCitationSources(
  messages: ReadonlyArray<MessageWithParts>,
): Map<string, CitationSource> {
  return citationSourcesOf(researchOutputsOf(messages));
}

/** The sources behind `ids`, in order; an id nothing resolves is left out. */
export function resolveCitationSources(
  ids: ReadonlyArray<string>,
  sources: ReadonlyMap<string, CitationSource>,
): CitationSource[] {
  const resolved: CitationSource[] = [];
  for (const id of new Set(ids)) {
    const source = sources.get(id);
    if (source) resolved.push(source);
  }
  return resolved;
}
